import { useRef, useState } from "react";
import { toast } from "sonner";
import { track, EVENTS } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export const PROJECT_TYPES = [
  "Brand Experience",
  "Website",
  "Interactive Experience",
  "3D / Motion",
  "WebAR / Spatial",
  "Data Experience",
  "Creative Technology",
  "Other",
];

export const SERVICE_OPTIONS = [
  "Digital Experiences",
  "Creative Technology",
  "3D Design & Motion",
  "Innovation Labs",
  "Interactive Solutions",
];

const EMPTY = {
  name: "",
  email: "",
  company: "",
  website: "",
  project_type: "",
  services: [],
  description: "",
  objective: "",
  audience: "",
  launch_date: "",
  budget: "",
  references: "",
  notes: "",
  honeypot: "",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const validateBrief = (form) => {
  const errors = {};
  if (form.name.trim().length < 2) errors.name = "Please enter your name.";
  if (!EMAIL_RE.test(form.email)) errors.email = "Please enter a valid email.";
  if (!form.project_type) errors.project_type = "Select a project type.";
  if (form.services.length === 0) errors.services = "Select at least one service.";
  if (form.description.trim().length < 20)
    errors.description = "Tell us a little more — at least 20 characters.";
  return errors;
};

const uploadAttachment = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/brief/upload`, { method: "POST", body: fd });
  if (!res.ok) throw new Error("upload failed");
  return res.json();
};

export const useBriefForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInput = useRef(null);

  const clearError = (key) =>
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    clearError(key);
  };

  const toggleService = (service) => {
    clearError("services");
    setForm((f) => ({
      ...f,
      services: f.services.includes(service)
        ? f.services.filter((s) => s !== service)
        : [...f.services, service],
    }));
  };

  const pickFile = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) {
      toast.error("File is larger than 10MB. Please attach a smaller file or share a link.");
      e.target.value = "";
      return;
    }
    setFile(picked);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateBrief(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error("Some details are missing. Check the highlighted fields.");
      return;
    }
    setSending(true);
    try {
      const attachment = file ? await uploadAttachment(file) : null;
      const res = await fetch(`${API}/brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, attachment }),
      });
      if (res.status === 429) {
        toast.error("Too many submissions from this connection. Please try again shortly.");
        return;
      }
      if (!res.ok) throw new Error("send failed");
      track(EVENTS.BRIEF_SUBMIT, { project_type: form.project_type });
      setSent(true);
      setForm(EMPTY);
      clearFile();
    } catch {
      toast.error("Transmission failed. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return {
    form,
    errors,
    file,
    fileInput,
    sending,
    sent,
    set,
    toggleService,
    pickFile,
    clearFile,
    submit,
    reset: () => setSent(false),
  };
};
